import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { $state, restManager } from 'app/ajs-upgraded-providers';
import { FidelityModuleSections } from '../../models/fidelity.models';
import { Transition } from 'angular-ui-router';
import { ToolbarLayoutService } from 'src/app/shared/services/toolbar-layout.service';
import { EntityManagerService, LoaderService, ToolbarEventsService } from 'src/app/core';
import { TranslateService } from '@ngx-translate/core';
import { FidelityModuleTabsComponent } from './fidelity-module-tabs/fidelity-module-tabs.component';
import { CommonModule } from '@angular/common';
import { UpgradeUiViewDirective } from 'src/ajs-to-angular';
import { FidelityCampaignsComponent } from '../fidelity-campaigns/fidelity-campaigns.component';
import { FidelityRulesComponent } from '../fidelity-rules/fidelity-rules.component';
import { FidelityPrizesComponent } from '../fidelity-prizes/fidelity-prizes.component';
import { FidelityDataCatalogService } from '../../services/fidelity-data-catalog.service';
import { ChainCampaigns } from 'tilby-models';
import { FidelityCampaignFormService } from '../../services/fidelity-campaign-form.service';
import { FidelityNavigationStateService } from '../../services/fidelity-navigation-state.service';
import { AlertDialogService, ConfirmDialogService } from 'src/app/dialogs';
import { FidelityRuleFormService } from '../../services/fidelity-rule-form.service';
import { FidelityPrizeFormService } from '../../services/fidelity-prize-form.service';

@Component({
  selector: 'app-fidelity-module',
  standalone: true,
  imports: [ 
    FidelityModuleTabsComponent, CommonModule, UpgradeUiViewDirective,
    FidelityCampaignsComponent, 
    FidelityRulesComponent,
    FidelityPrizesComponent
  ],
  providers: [
    FidelityCampaignFormService,
    FidelityRuleFormService,
    FidelityPrizeFormService,
    FidelityNavigationStateService
  ],
  templateUrl: './fidelity-module.component.html',
  styleUrl: './fidelity-module.component.scss'
})
export class FidelityModuleComponent implements OnInit, OnDestroy {

  private readonly state = inject($state);
  private readonly translate = inject(TranslateService);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly toolbarLayoutService = inject(ToolbarLayoutService);
  private readonly fidelityDataCatalogService = inject(FidelityDataCatalogService);
  private readonly loaderService = inject(LoaderService);
  private readonly entityManagerService = inject(EntityManagerService);
  private readonly restManagerService = inject(restManager);
  private readonly fidelityNavigationStateService = inject(FidelityNavigationStateService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly alertDialogService = inject(AlertDialogService);

  private unsubscribeOnBefore: Function | undefined;
  private unsubscribeOnSuccess: Function | undefined;
  protected isNavigating = false;
  protected isLoading = false;

  modulePath = this.state.$current.name.includes('app.new.fidelity');

  tabChange: number = -1;
  tabActive: FidelityModuleSections | null = null;
  private isInternalTabReset = false;
  private loadFailed = false;

  constructor() {
    this.toolbarEventsService.moduleTitle.next("FIDELITY");

    // --- HOOK PREVENTIVO ---
    this.unsubscribeOnBefore = this.state.router.transitionService.onBefore(
      { from: 'app.new.fidelity.**' }, 
      async (transition: Transition) => {
        if (transition.to().name === transition.from().name) {
          return true;
        }
        if (this.fidelityNavigationStateService.hasUnsavedChanges()) {
          const confirmed = await this.confirmDialogService.openDialog({
            data: {
              messageLabel: 'FIDELITY.SETUP.CAMPAIGNS.EDIT_CANCEL_CONFIRM',
              confirmLabel: 'APPLICATION.YES',
              cancelLabel: 'APPLICATION.NO'
            },
            disableClose: true
          });

          if (!confirmed) {
            this.isInternalTabReset = true;
            const currentValidTab = this.tabChange;
            this.tabChange = -1;
            
            setTimeout(() => {
              this.tabChange = currentValidTab;
              // when the tab group does not emit (e.g. already on the first tab) the flag would
              // stay set and swallow the next tab click
              setTimeout(() => this.isInternalTabReset = false);
            });

            return false; // Blocca la navigazione
          }
          return true; // Procede
        }
        return true; // Nessuna modifica, procedi pure
      }
    );

    // --- HOOK DI SUCCESSO ---
    this.unsubscribeOnSuccess = this.state.router.transitionService.onSuccess({}, (transition: Transition&any) => {
      const stateName = transition.to().name;
      this.modulePath = stateName?.includes('app.new.fidelity');
      this.syncTabWithState(stateName);
    })
  }

  async ngOnInit() {
    await this.loadData();
    // without data the tabs would render empty lists: the error dialog has already been shown
    if (this.loadFailed) return;
    this.syncTabWithState(this.state?.current?.name);
  }

  async loadData() {
    this.loadFailed = false;
    this.loaderService.show();
    this.isLoading = true;

    try {
      const [
        campaigns, rules, prizes, items
      ] = await Promise.all([
        this.entityManagerService.campaigns.fetchCollectionOnline({}),
        this.restManagerService.getList("rules", { pagination: false }),
        this.restManagerService.getList("prizes", { pagination: false }),
        this.entityManagerService.items.fetchCollectionOffline()
      ]);

      // this.loaderService.hide();
      // await new Promise(resolve => setTimeout(resolve, 500000))

      this.fidelityDataCatalogService.setProcessedItems(items);

      this.fidelityDataCatalogService.addDataToCatalog('campaigns', campaigns as ChainCampaigns[] || []);
      this.fidelityDataCatalogService.setProcessedRules(rules);
      this.fidelityDataCatalogService.setProcessedPrizes(prizes);

    } catch (error) {
      this.loadFailed = true;
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('FIDELITY.SETUP.LOAD_ERROR')
        }
      });
    } finally {
      this.loaderService.hide();
      this.isLoading = false;
    }
  }

  private syncTabWithState(stateName: string) {
    if (!stateName) return;

    let newTabChange = -1;
    let newTabActive: FidelityModuleSections | null = null;

    if (stateName.includes('campaigns')) {
      newTabActive = 'campaigns';
      newTabChange = 0;
    } else if (stateName.includes('rules')) {
      newTabActive = 'rules';
      newTabChange = 1;
    } else if (stateName.includes('prizes')) {
      newTabActive = 'prizes';
      newTabChange = 2;
    } 
    
    if (newTabChange === -1 && stateName === 'app.new.fidelity') {
      if(!this.isNavigating) {
        this.isNavigating = true;
        this.state.go('app.new.fidelity.campaigns').finally(() => this.isNavigating = false);
        return;
      }
    }

    if (this.tabChange !== newTabChange) {
      this.tabActive = newTabActive;
      this.tabChange = newTabChange;
    }
  }

  changeTab(textLabelPanel: any) {
    if (this.isInternalTabReset) {
      this.isInternalTabReset = false;
      // console.log("changeTab: Reset grafico intercettato, non navigo.");
      return;
    }

    if (this.isLoading || this.isNavigating || this.state.router.globals.transition) {
      // console.log("changeTab: navigazione bloccata (isNavigating o isLoading o || this.state.router.globals.transition)");
      return;
    }

    let targetRoute: string | null = null;

    if (textLabelPanel === this.translate.instant("FIDELITY.SETUP.CAMPAIGNS.TITLE")) {
      targetRoute = 'app.new.fidelity.campaigns';
    }
    else if (textLabelPanel === this.translate.instant("FIDELITY.SETUP.RULES.TITLE")) {
      targetRoute = 'app.new.fidelity.rules';
    }
    else if (textLabelPanel === this.translate.instant("FIDELITY.SETUP.PRIZES.TITLE")) {
      targetRoute = 'app.new.fidelity.prizes';
    }

    if (targetRoute && (this.state.is(targetRoute) || this.state.current.name === targetRoute)) {
      // console.log("Navigazione bloccata: sono già su questa rotta.");
      return; 
    }

    this.isNavigating = true;
    this.state.go(targetRoute)
      .catch((error: any) => {})
      .finally(() => {
      setTimeout(() => {
        this.isNavigating = false;
      });
    });
  }

  ngOnDestroy(): void {
    if (this.unsubscribeOnBefore) {
      this.unsubscribeOnBefore();
    }
    if (this.unsubscribeOnSuccess) {
      this.unsubscribeOnSuccess();
    }
    this.toolbarLayoutService.showModuleTitle$.next(true);
  }
}
